/**
 * React hooks for agent tasks with HITL approval workflow
 *
 * This is separate from useAgentTasks.ts which handles workflow node tasks.
 * This hook manages agent-created tasks that require human approval before execution.
 *
 * STEP 2: System message emissions are centralized here to eliminate duplicates.
 * Both chat and panel approvals use this hook, ensuring single source of truth.
 */

import useSWR from "swr";
import { useState } from "react";
import * as agentTasksApi from "../api/agentTasks";
import type {
  AgentTask,
  CreateTaskPayload,
  ListTasksParams,
  RejectTaskPayload,
} from "../api/agentTasks";
import { agentUiBridge } from "../lib/agentUiBridge";

/**
 * Hook for listing agent tasks
 */
export function useAgentTasksHitl(params: ListTasksParams = {}) {
  const key = `/agent-tasks?${new URLSearchParams(
    Object.entries(params).reduce((acc, [k, v]) => {
      if (v !== undefined) acc[k] = String(v);
      return acc;
    }, {} as Record<string, string>)
  ).toString()}`;

  const { data, error, mutate, isLoading } = useSWR<AgentTask[]>(
    key,
    () => agentTasksApi.listTasks(params),
    {
      refreshInterval: 5000, // Refresh every 5 seconds for real-time updates
      revalidateOnFocus: true,
    }
  );

  return {
    tasks: data,
    isLoading,
    error,
    mutate,
  };
}

/**
 * Hook for HITL inbox (pending approval tasks)
 */
export function useAgentTaskInbox(agent?: string, limit?: number) {
  const key = `/agent-tasks/pending-approval?agent=${agent || ""}&limit=${limit || 50}`;

  const { data, error, mutate, isLoading } = useSWR<AgentTask[]>(
    key,
    () => agentTasksApi.getPendingApproval(agent, limit),
    {
      refreshInterval: 3000, // Refresh every 3 seconds for inbox
      revalidateOnFocus: true,
    }
  );

  return {
    pendingTasks: data,
    isLoading,
    error,
    mutate,
  };
}

/**
 * Hook for a single task
 */
export function useAgentTaskHitl(taskId: string | null) {
  const { data, error, mutate, isLoading } = useSWR<AgentTask>(
    taskId ? `/agent-tasks/${taskId}` : null,
    () => (taskId ? agentTasksApi.getTask(taskId) : null),
    {
      revalidateOnFocus: true,
    }
  );

  return {
    task: data,
    isLoading,
    error,
    mutate,
  };
}

/**
 * Hook for task actions (create, approve, reject)
 * STEP 2: Centralized system message emissions - single source of truth
 */
export function useAgentTaskActions() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createTask = async (payload: CreateTaskPayload): Promise<AgentTask | null> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const task = await agentTasksApi.createTask(payload);
      return task;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const approveTask = async (taskId: string, task?: AgentTask): Promise<AgentTask | null> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await agentTasksApi.approveTask(taskId);

      // Emit system message to chat (single source of truth)
      if (result) {
        const taskLabel = result.task_type ? getTaskTypeLabel(result.task_type) : '';
        const message = `✅ Approved: ${result.title}${taskLabel ? ` (${taskLabel})` : ''} — running...`;
        agentUiBridge.emit({
          type: "chat.appendSystemMessage",
          agent: result.agent,
          text: message,
          metadata: { taskId: result.id, action: "approved" },
        });
      } else if (task) {
        // Fallback if result doesn't include agent (use provided task)
        const taskLabel = task.task_type ? getTaskTypeLabel(task.task_type) : '';
        const message = `✅ Approved: ${task.title}${taskLabel ? ` (${taskLabel})` : ''} — running...`;
        agentUiBridge.emit({
          type: "chat.appendSystemMessage",
          agent: task.agent,
          text: message,
          metadata: { taskId: task.id, action: "approved" },
        });
      }

      return result;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const rejectTask = async (
    taskId: string,
    payload: RejectTaskPayload = {},
    task?: AgentTask
  ): Promise<AgentTask | null> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await agentTasksApi.rejectTask(taskId, payload);

      // Emit system message to chat (single source of truth)
      if (result) {
        const taskLabel = result.task_type ? getTaskTypeLabel(result.task_type) : '';
        const message = `❌ Rejected: ${result.title}${taskLabel ? ` (${taskLabel})` : ''}`;
        agentUiBridge.emit({
          type: "chat.appendSystemMessage",
          agent: result.agent,
          text: message,
          metadata: { taskId: result.id, action: "rejected" },
        });
      } else if (task) {
        // Fallback if result doesn't include agent (use provided task)
        const taskLabel = task.task_type ? getTaskTypeLabel(task.task_type) : '';
        const message = `❌ Rejected: ${task.title}${taskLabel ? ` (${taskLabel})` : ''}`;
        agentUiBridge.emit({
          type: "chat.appendSystemMessage",
          agent: task.agent,
          text: message,
          metadata: { taskId: task.id, action: "rejected" },
        });
      }

      return result;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    createTask,
    approveTask,
    rejectTask,
    isSubmitting,
    error,
  };
}

/**
 * Get task status badge color
 */
export function getTaskStatusColor(status: string): string {
  switch (status) {
    case "pending_approval":
    case "pending":
      return "yellow";
    case "approved":
      return "blue";
    case "executing":
      return "purple";
    case "completed":
      return "green";
    case "rejected":
      return "red";
    case "failed":
      return "red";
    default:
      return "gray";
  }
}

/**
 * Get human-readable task status
 */
export function getTaskStatusLabel(status: string): string {
  switch (status) {
    case "pending_approval":
      return "Pending Approval";
    case "approved":
      return "Approved";
    case "executing":
      return "Executing";
    case "completed":
      return "Completed";
    case "rejected":
      return "Rejected";
    case "failed":
      return "Failed";
    default:
      return status;
  }
}

/**
 * Get agent display name
 */
export function getAgentDisplayName(agent: string): string {
  switch (agent) {
    case "booking":
      return "Booking Agent";
    case "marketing":
      return "Marketing Agent";
    case "publicist":
      return "Publicist Agent";
    case "crm":
      return "CRM Agent";
    case "manager":
      return "Manager Agent";
    case "email":
      return "Email Agent";
    default:
      return agent;
  }
}

/**
 * Format task type for display
 */
export function getTaskTypeLabel(taskType: string): string {
  return taskType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
